/* =========================== MG WISHLIST-KING SCROLL SHIM — BEGIN ===========================
   Made Good — 14 Aug 2026 — STEP 3 of the header scroll-smoothness work.
   Revised 15 Aug 2026 (v2) after reading the vendor docs — see "WHAT THE DOCS CHANGED".

   >>> ROLLBACK STEP 3: delete everything between this BEGIN marker and the matching END
       marker below. Nothing else in this file belongs to Step 3. <<<

   THE VENDOR, for whoever reads this next:
   our wishlist app ("Set Aside") is Wishlist King by Appmate, now rebranded SWISH.
   Docs: docs.swish.app (help.appmate.io redirects there) · developers.swish.app ·
   full page index at docs.swish.app/llms.txt · support@swish.app.
   ⚠️ docs.appmate.cloud is a DIFFERENT, UNRELATED COMPANY. Nothing there applies to us.
   The theme still carries the old branding: @appmate/wishlist@5.29.0, cdn.appmate.io/themecode/
   made-good-ltd/, and a Shopify extension named swish-wishlist-king-254.

   THE PROBLEM (measured live on V10, collection page, 100 product cards)
   Swish registers EIGHT HUNDRED scroll/resize listeners:
       200 scroll  on window          200 resize  on window
       200 scroll  on visualViewport  200 resize  on visualViewport
   all of them the same `() => { this.updatePosition() }`, one pair per component instance.
   So every single scroll event dispatches to 200 separate listeners, four times over.

   WHAT IT COSTS. On Will's Mac this is nearly invisible, which is exactly why it was never
   caught — but throttle the CPU 4x (an ordinary mid-range laptop) and it is the difference
   between 60fps and not:

       scripted scroll, 4x CPU throttle, median frame time
         with Swish as-is ......... 26.2ms   (frames over 25ms: 89 of 147)
         its listeners removed .... 16.8ms   (frames over 25ms: 11 of 147)
         with v1 of this shim ..... 17.0ms   (frames over 25ms: 40 of 147)
         with v2 (this file) ...... 17.1ms   (frames over 25ms: 13 of 147)

   =============================================================================================
   WHAT THE DOCS CHANGED (15 Aug 2026) — why v2 exists
   =============================================================================================
   The Collection Buttons page says these buttons "float on top of the product images",
   positioned by JS against a reference element derived from a product-link CSS selector. There
   is no documented placement mode that avoids JS positioning — so the listeners are BY DESIGN,
   not a misconfiguration, and deleting them is not safe. Confirmed in the DOM: each card button
   is `wk-button.wk-floating` inside `<wishlist-button-collection>`, carrying an app-computed
   inline `position:absolute; transform: translate3d(305px,0,0)`.

   ⭐ BUT: that button is positioned absolutely INSIDE THE CARD. Scrolling cannot change where it
   sits relative to its offsetParent. Sampled its inline style at scrollY 0 / 600 / 1400 / back
   to 0 — BYTE-IDENTICAL every time. Only a resize can move it.

   Verified across every template that carries wishlist components, all elements measured for
   real (rect delta over a 400-500px scroll):
       collection (100 cards) — 412 Swish elements, 0 viewport-fixed
       product                — 81 Swish elements, 0 viewport-fixed; 26 of 26 visible ones
                                moved WITH the page, 0 stayed put
       Set Aside page         — 18 Swish elements, 0 viewport-fixed
   So on this store, every scroll-driven reposition is wasted work.

   The one Swish component that WOULD need scroll is their "Floating Wishlist Link" theme
   extension, which is anchored to a browser corner. We do not use it — our header link is the
   inline "Instant Header Link" pattern, whose counter is absolutely positioned and, per their
   docs, involves no scroll-dependent positioning. v2 does NOT assume that stays true: it
   detects viewport-fixed Swish elements at runtime and turns scroll handling back on by itself
   if one ever appears (see THE GUARD).

   WHAT v2 DOES
   Same batching as v1 — 800 registrations collapsed into FOUR real listeners, one per
   (target, type), each coalesced to one requestAnimationFrame — PLUS: once it has proven no
   Swish element is viewport-fixed, the two SCROLL groups return immediately and do no work at
   all. Resize is untouched and always runs, because resize is the event that actually matters.

   THE GUARD — how it decides, and why it cannot get stuck wrong
   ⋄ Scroll handling starts ENABLED. It is only ever switched off by positive evidence.
   ⋄ 3s after init (Swish mounts ~1.3s), it scans every Swish element once for
     `position: fixed`. None found → scroll handling off. One found → stays on permanently.
   ⋄ Re-evaluated on resize (a breakpoint could reveal a fixed element) and whenever a node is
     added to <body> (where a floating link would mount). Both debounced; the scan is one-off,
     never per frame, and runs in an idle callback where available.
   ⋄ So if anyone enables the Floating Wishlist Link in the Swish admin later, this corrects
     itself with no code change. It fails SAFE, in the direction of doing the work.

   SAFETY
   ⋄ Only intercepts scroll/resize, only on window and visualViewport, and only when the call
     stack names appmate/wishlist-element. Every other listener on the page is untouched.
   ⋄ removeEventListener is patched to match, so Swish can still deregister — otherwise its
     components would leak.
   ⋄ Every patched path is wrapped so any failure falls straight through to the native method.
     If this shim breaks, the page behaves exactly as it does today.
   ⋄ Kill switch for A/B testing without a rollback: add ?mgwkshim=off to any URL.
   ⋄ Verify in the console: window.__mgWkShim should read
     {captured: 800, groups: 4, scrollMatters: false, evaluated: true} on a collection page.
     `captured: 0` means it loaded too late to matter — a silent no-op, not a breakage.
     `scrollMatters: true` means it found a viewport-fixed component and kept scroll on.

   PARKED: reporting this upstream to support@swish.app. The useful, accurate framing is not
   "you have a bug" but "floating collection buttons recompute position on scroll, which cannot
   change their position when they are anchored inside the card; on a 100-product grid that is
   400 redundant listeners and it costs a mid-range laptop 60fps."
   =========================================================================================== */
(function () {
  'use strict';
  try {
    if (/[?&]mgwkshim=off/.test(location.search)) return;
    if (!window.requestAnimationFrame) return;

    var origAdd = EventTarget.prototype.addEventListener;
    var origRemove = EventTarget.prototype.removeEventListener;
    var groups = new Map();
    var stats = { captured: 0, groups: 0, scrollMatters: null, evaluated: false };
    window.__mgWkShim = stats;

    var idle = window.requestIdleCallback || function (fn) { return setTimeout(fn, 1); };

    function isTarget(t) {
      return t === window || (window.visualViewport && t === window.visualViewport);
    }
    function keyFor(t, type) {
      return (t === window ? 'w' : 'vv') + ':' + type;
    }
    function isWishlistCaller() {
      var st = '';
      try { st = new Error().stack || ''; } catch (e) { return false; }
      return /appmate|wishlist-element/i.test(st);
    }

    /* Does anything Swish renders actually need repositioning on scroll? Only a viewport-fixed
       element can. One-off scan, never per frame. Fails safe: any doubt leaves scroll ON. */
    function evaluateScrollNeed() {
      try {
        var els = document.querySelectorAll(
          'wishlist-link, wishlist-button-product, wishlist-button-collection, wishlist-add-to-cart,' +
          ' wishlist-share, wk-button, wk-icon, [class*="wk-"], [class*="wkh-"]'
        );
        for (var i = 0; i < els.length; i++) {
          if (getComputedStyle(els[i]).position === 'fixed') {
            stats.scrollMatters = true;   // e.g. the Floating Wishlist Link — keep scroll on
            stats.evaluated = true;
            return;
          }
        }
        stats.scrollMatters = false;
        stats.evaluated = true;
      } catch (e) {
        stats.scrollMatters = true;       // on any error, keep doing the work
        stats.evaluated = true;
      }
    }

    var evalTimer = null;
    function scheduleEvaluate(delay) {
      clearTimeout(evalTimer);
      evalTimer = setTimeout(function () { idle(evaluateScrollNeed); }, delay);
    }

    // Swish mounts around 1.3s; give it comfortable headroom before deciding.
    scheduleEvaluate(3000);

    EventTarget.prototype.addEventListener = function (type, fn, opts) {
      try {
        if ((type === 'scroll' || type === 'resize') && isTarget(this) &&
            typeof fn === 'function' && isWishlistCaller()) {
          var k = keyFor(this, type);
          var g = groups.get(k);
          if (!g) {
            g = { target: this, type: type, fns: [], scheduled: false };
            groups.set(k, g);
            stats.groups++;
            origAdd.call(this, type, function (ev) {
              // Proven unnecessary for this page -> do nothing at all.
              if (g.type === 'scroll' && stats.scrollMatters === false) return;
              if (g.type === 'resize') scheduleEvaluate(500);  // layout changed, re-decide
              if (g.scheduled) return;
              g.scheduled = true;
              requestAnimationFrame(function () {
                g.scheduled = false;
                for (var i = 0; i < g.fns.length; i++) {
                  try { g.fns[i].call(g.target, ev); } catch (e) {}
                }
              });
            }, { passive: true });
          }
          if (g.fns.indexOf(fn) === -1) g.fns.push(fn);
          stats.captured++;
          return;
        }
      } catch (e) { /* fall through to native below */ }
      return origAdd.apply(this, arguments);
    };

    EventTarget.prototype.removeEventListener = function (type, fn, opts) {
      try {
        if (type === 'scroll' || type === 'resize') {
          var g = groups.get(keyFor(this, type));
          if (g) {
            var i = g.fns.indexOf(fn);
            if (i !== -1) { g.fns.splice(i, 1); return; }
          }
        }
      } catch (e) { /* fall through to native below */ }
      return origRemove.apply(this, arguments);
    };

    /* A Floating Wishlist Link would mount as a child of <body>. childList only, no subtree —
       cheap. If the page grows a new top-level node, re-decide rather than stay stale. */
    try {
      if (window.MutationObserver && document.body) {
        new MutationObserver(function (records) {
          for (var i = 0; i < records.length; i++) {
            if (records[i].addedNodes && records[i].addedNodes.length) {
              stats.scrollMatters = null;   // unknown again = scroll handling back ON
              scheduleEvaluate(1000);
              return;
            }
          }
        }).observe(document.body, { childList: true });
      }
    } catch (e) {}
  } catch (e) { /* shim is entirely optional -- never let it break the page */ }
})();
/* ============================ MG WISHLIST-KING SCROLL SHIM — END ============================ */

/* ------------------------------------------------------------------------------------------
   MG 16 Aug 2026 (Will sss) — FIXED-WIDTH BRACKET COUNTS: [1] must be the same width as [0].

   Will: "can the brackets '[1]' as used for header items count and in the Filter button and
   draw, be fixed 'wider' width … make the brackets wide enough so they don't change. the
   exception will be when item count goes to double digits; again make the brackets fixed at
   widest [88] not [11]."

   ⚠️ SCOPE: all four counts now — Cart and Set Aside in the header, plus the floating FILTER
   button and the drawer's FILTER title. The filter pair was attempted earlier on 16 Aug, broke
   the button, and was reverted; it was re-done the same day once the real cause was found. The
   cause was NOT flex layout: assets/mg-filter-chat.css carries
   `.fillter_button, .fillter_button span { width:158px !important; height:46px !important }` —
   a DESCENDANT selector with !important — so the nested <span> the first attempt used was forced
   to the full size of the button and nothing could override it. The count is now an <i>, which
   that rule does not match. Full write-up at the top of assets/mg-filter-drawer.js.

   ⚠️ THE ONE-LINE FIX DOES NOT WORK. `font-variant-numeric: tabular-nums` changes NOTHING here,
   because **Neue Haas Grotesk Text W01 has no tabular figures**. Measured, with and without,
   byte-identical widths. The digits really are proportional (13px):

       "0"  9.17px   <- the WIDEST digit
       "1"  5.62px   <- the narrowest
       "11" 11.23px      "88" 17.33px

   ⭐ That "0" is the widest digit is the useful fact for a SINGLE character: reserving its advance
   is exactly right, no more and no less, and it scales with font-size so one rule survives every
   breakpoint. (We do not write the `ch` unit literally below; the clone measurement gets the same
   answer and, unlike `ch`, also accounts for letter-spacing and for bracket pseudo-elements.)

   ⭐⭐ THE 10-19 RULE (Will, 16 Aug, after seeing [13] and [17] on screen).
   Reserving "00" for a two-digit count is technically correct and visually wrong. It is 3.83px
   wider than anything that will actually render, and "[ 13 ]" read as airy — the brackets looked
   detached from their digits. Will: *"if there are double digits they will almost always be
   between 10 and 19 (so starting with '1'). make it fit this range."* He is right about the data:
   a cart, a Set Aside list or a filter selection that reaches 20 is rare, and 100 is not a real
   case at all.
   So the reserved string puts a literal "1" in the FIRST position and the widest character in the
   rest: "10" rather than "00". At 16px in the drawer title that is 25.05px instead of 28.61px.
   ⚠️ This is a FLOOR, not a cap — `min-width` cannot clip anything. A genuine 20+ simply renders
   ~2.6px wider than the reservation and its brackets sit flush. That is the correct trade: the
   common case is tight, the rare case degrades by a hair rather than the common case paying for
   the rare one permanently. Single characters are unchanged.

   HOW IT SIZES — measure a clone, never the live element.
   For each counter we clone it, set the clone's text to the widest equivalent string, measure, and
   set that as `min-width` on the real element. Cloning matters for two reasons:
     ⋄ the Set Aside counter's brackets are ::before/::after, so only a real clone of the element
       measures the bracketed width. A text probe cannot see them.
     ⋄ the Set Aside counter is rendered by Swish through lit-html. We must never write to its
       content — the clone takes the abuse, the live node is only ever given a style.
   The clone is inserted next to the original so it inherits the same font, and removed in the same
   synchronous tick, so nothing else can ever observe it.

   ⚠️ MODE IS DECLARED PER TARGET, NOT INFERRED FROM getComputedStyle. The first version read the
   element's computed `display` and branched on it — and read the wrong thing, because a broad
   parent selector was also matching the count. Declare the shape you want; do not ask the cascade
   what it already is.

   ⚠️ AND THE TRAP THAT NEARLY GOT THROUGH: when the filter-button version was broken, the widths
   were still CONSTANT across [0]/[1]/[8]/[+] — so a "does the width change?" check PASSED while
   the button was visibly wrong on screen. Screenshot it; do not just measure it.

   THE SHAPES
     wishlist-link .wk-counter          Set Aside. display:flex, brackets are ::before/::after,
                                        the digit is a bare text node. mode 'flex' → keep the
                                        display and add `justify-content: space-between`, so the
                                        two brackets pin to the edges of the reserved box and the
                                        digit centres between them. Measured 19.44px, constant
                                        across 0/1/8, and the whole header link stays 88.96px.
     .header-cart__count.cart__count--text   Cart. Digits already in their own inline span, with
                                        the brackets as literal text either side. mode 'inline' →
                                        forced to inline-block, since min-width does nothing on an
                                        inline box. Measured 9.18px, constant. (Note the second
                                        class — `.header-cart__count` alone also matches a
                                        display:none badge span.)
     .mg-cnt                            The FILTER button and the drawer's FILTER title, both
                                        written by assets/mg-filter-drawer.js. Same shape as Set
                                        Aside — one <i>, brackets as ::before/::after, laid out
                                        inline-flex by mg-filter-drawer.css — so mode 'flex'.
                                        `chars: '0+'` because this one also renders "[+]" when no
                                        filters are applied, and "+" must be measured too.
                                        Measured 19.31px for one character and 25.05px for two,
                                        constant across + / 0 / 1 / 8 / 10 / 13 / 17 / 19.

   STAYING CORRECT AFTER THE FIRST PASS
     ⋄ a MutationObserver per element re-measures when the count changes (cart add, wishlist add,
       a filter ticked).
       Observing the element only — the clone goes in the PARENT, so it can never retrigger the
       observer that measured it.
     ⋄ a bounded poll (400ms × 25 = 10s) catches elements that appear late: Swish hydrates ~1.3s.
     ⋄ debounced resize re-measures from scratch, because font-size changes at breakpoints.
     ⋄ scan() is exported as window.__mgCounts.scan so a script that CREATES a counter after that
       poll has finished can ask for it to be picked up. mg-filter-drawer.js calls it when the
       collection AJAX rebuilds the FILTER button.
   Nothing here runs per frame or per scroll.

   FAILS SAFE: if any of it throws, the counters render exactly as they do today.
   Verify in the console: window.__mgCounts.sized should be > 0 on any page with a header count.

   >>> ROLLBACK: delete this whole block. <<<
   ------------------------------------------------------------------------------------------ */
(function () {
  'use strict';
  try {
    var TARGETS = [
      { sel: 'wishlist-link .wk-counter',             chars: '0',  mode: 'flex'   },
      { sel: '.header-cart__count.cart__count--text', chars: '0',  mode: 'inline' },
      { sel: '.mg-cnt',                               chars: '0+', mode: 'flex', reset: true }
    ];
    var stats = { sized: 0, watched: 0 };
    window.__mgCounts = stats;

    function neutralise(el, important) {
      var p = important ? 'important' : '';
      el.style.setProperty('padding', '0', p);
      el.style.setProperty('margin', '0', p);
      el.style.setProperty('flex', '0 0 auto', p);
      el.style.setProperty('gap', '0', p);
    }

    function measure(t, el, str) {
      var clone = el.cloneNode(true);
      clone.textContent = str;
      clone.style.setProperty('position', 'absolute', 'important');
      clone.style.setProperty('visibility', 'hidden', 'important');
      clone.style.setProperty('min-width', '0', 'important');
      clone.style.setProperty('width', 'auto', 'important');
      clone.style.setProperty('pointer-events', 'none', 'important');
      clone.style.setProperty('top', '0', 'important');
      clone.style.setProperty('left', '0', 'important');
      /* Measure the clone in the SAME shape the real element will end up in, or the number is
         meaningless — see the MODE note above. */
      if (t.mode === 'inline') clone.style.setProperty('display', 'inline-block', 'important');
      if (t.reset) neutralise(clone, true);
      var host = el.parentNode || document.body;
      host.appendChild(clone);
      var w = clone.getBoundingClientRect().width;
      host.removeChild(clone);
      return w;
    }

    /* The string to reserve for, at a given length. One character -> just the widest candidate.
       Two or more -> a literal "1" in the first position, widest candidate in the rest ("10", not
       "00"). See THE 10-19 RULE above for why, and why overflowing on a real 20+ is fine. */
    function widestString(chars, i, n) {
      var c = chars.charAt(i);
      return n > 1 ? '1' + new Array(n).join(c) : c;
    }

    function size(t, el) {
      try {
        var txt = (el.textContent || '').trim();
        var n = txt.length;
        if (!n) { el.style.removeProperty('min-width'); return; }
        var widest = 0;
        for (var i = 0; i < t.chars.length; i++) {
          var w = measure(t, el, widestString(t.chars, i, n));
          if (w > widest) widest = w;
        }
        if (!widest) return;
        if (t.reset) neutralise(el, false);
        if (t.mode === 'flex') {
          el.style.setProperty('justify-content', 'space-between');
        } else {
          el.style.setProperty('display', 'inline-block');
          el.style.setProperty('text-align', 'center');
        }
        el.style.setProperty('min-width', (Math.ceil(widest * 100) / 100) + 'px');
        stats.sized++;
      } catch (e) {}
    }

    function watch(t, el) {
      if (el.__mgCntWatched) return;
      el.__mgCntWatched = true;
      stats.watched++;
      size(t, el);
      try {
        new MutationObserver(function () { size(t, el); })
          .observe(el, { childList: true, characterData: true, subtree: true });
      } catch (e) {}
    }

    function scan() {
      TARGETS.forEach(function (t) {
        Array.prototype.forEach.call(document.querySelectorAll(t.sel), function (el) {
          watch(t, el);
        });
      });
    }

    function remeasure() {
      TARGETS.forEach(function (t) {
        Array.prototype.forEach.call(document.querySelectorAll(t.sel), function (el) {
          el.style.removeProperty('min-width');
          size(t, el);
        });
      });
    }

    /* Exported so a late-created counter can be adopted after the poll below has stopped.
       Guarded at every call site, so removing it degrades to "renders unreserved", never worse. */
    stats.scan = scan;

    scan();
    var tries = 0;
    var poll = setInterval(function () {
      scan();
      if (++tries > 25) clearInterval(poll);
    }, 400);
    window.addEventListener('load', scan);

    var rt = null;
    window.addEventListener('resize', function () {
      clearTimeout(rt);
      rt = setTimeout(remeasure, 200);
    }, { passive: true });
  } catch (e) { /* purely cosmetic — never let it break the header */ }
})();

/* ------------------------------------------------------------------------------------------
   MG 15 Aug 2026 -- EAGER FIRST-ROW IMAGES: a redundant safety sweep.
   Will: "when I load a collpg the brown frame for the product image loads then the image pops
   in late, which looks cheap and ugly."

   Companion to the mg_eager branch in snippets/image-element.liquid and the mg_eager_limit in
   snippets/product-loop.liquid. Those give the first 4 cards of a collection page a real
   src/srcset so the download starts at parse time instead of waiting ~567ms for lazysizes.

   ⚠️ THIS BLOCK IS NOT THE MECHANISM. The eager images carry theme.css's `transition--fade-in`,
   which is `opacity: 0` until something adds `.lazyloaded`, and lazysizes never sees them — so
   the reveal is done by an INLINE `onload` attribute on the tag itself, in image-element.liquid.
   An earlier version of this fix did the reveal from here and that was WRONG: this file is a
   DEFERRED script queued behind theme.js, so on a slow connection the image landed and then sat
   at opacity 0 waiting for a script that had not run yet. Sampled on Slow 3G: image complete at
   6222ms, script not until DCL at 8995ms. We had made the image arrive sooner and then hidden it
   for longer. Never gate a load-critical reveal on a deferred script.

   What remains here is only a backstop for the case where a load event is somehow missed
   entirely. It should never be the thing that reveals an image; if it ever is, the inline handler
   has been lost from image-element.liquid.

   ⚠️ 16 Aug: this covers only the first FOUR cards. Everything below the fold was still blank for
   ~1000ms on scroll — see the mg-card-image-preload.js loader at the bottom of this file.
   ------------------------------------------------------------------------------------------ */
(function () {
  var imgs = document.querySelectorAll('img[data-mg-eager]');
  if (!imgs.length) return;

  function reveal(img) {
    if (img.__mgRevealed) return;
    img.__mgRevealed = true;
    img.classList.add('lazyloaded');
  }

  setTimeout(function () { Array.prototype.forEach.call(imgs, reveal); }, 5000);
})();

/* Made Good -- grid first-paint fade (28 Jul 2026, toned down same day)
   See project folder page-load-premium-feel-brief-2026-07-28.md item #3.

   ORIGINAL VERSION (this session, earlier): scroll-triggered stagger with
   opacity + a 16px upward drift, re-firing via IntersectionObserver as new
   rows entered the viewport. Will found it distracting -- likely the
   combination of movement + repeatedly re-triggering on every scroll.

   REVISED to be much quieter: opacity-ONLY (no movement), applied ONCE at
   page load, ONLY to cards already visible in the initial viewport. Cards
   below the fold render normally with no animation at all, ever -- there
   is no scroll-triggered re-fire anymore. This only touches your very
   first glance at a grid, never recurs while browsing.

   Reuses the mega-menu's --mg-mm-ease / --mg-mm-textdur custom properties
   (read live via getComputedStyle) rather than inventing new timing
   language. Driven as inline styles with 'important' priority so it can't
   lose to any other rule on .product__grid-item, whatever the specificity.

   Site-wide, tiny, no-ops if no .product__grid-item exists on the page,
   and does nothing at all if prefers-reduced-motion is set.

   ⚠️ MG 15 Aug 2026: this fades the WHOLE CARD, including the image inside it, and the images
   now fade themselves (see the eager block above and mg-card-press-fix.css). Two nested opacity
   fades multiply -- 0.5 x 0.5 = 0.25 -- so the combined reveal is slower and mushier than either
   alone. Left in place for now and being judged on screen; if it reads soft, the answer is to
   drop this card fade rather than to shorten the image one, because the image fade is the part
   that actually solves the pop-in Will reported. */
(function () {
  var rootStyle = getComputedStyle(document.documentElement);
  var DUR = (rootStyle.getPropertyValue('--mg-mm-textdur') || '').trim() || '300ms';
  var EASE = (rootStyle.getPropertyValue('--mg-mm-ease') || '').trim() || 'cubic-bezier(0.16,1,0.3,1)';
  var reduceMotion = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

  function init() {
    if (reduceMotion) return; // leave everything exactly as rendered, no animation at all

    var items = document.querySelectorAll('.product__grid-item');
    if (!items.length) return;

    var viewportHeight = window.innerHeight;
    var aboveFold = [];
    Array.prototype.forEach.call(items, function (el) {
      if (el.getBoundingClientRect().top < viewportHeight) aboveFold.push(el);
    });
    if (!aboveFold.length) return;

    aboveFold.forEach(function (el, i) {
      var delay = (i % 4) * 60; // one row's worth of stagger, then repeats
      el.style.setProperty(
        'transition',
        'opacity ' + DUR + ' ' + EASE + ' ' + delay + 'ms',
        'important'
      );
      el.style.setProperty('opacity', '0', 'important');
    });

    // Force layout so the 0-opacity state is committed before animating to 1 --
    // otherwise both writes can get coalesced into one frame with no visible fade.
    void aboveFold[0].offsetHeight;

    requestAnimationFrame(function () {
      aboveFold.forEach(function (el) {
        el.style.setProperty('opacity', '1', 'important');
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* ------------------------------------------------------------------------------------------
   MG 14 Aug 2026 -- loader for assets/mg-filter-drawer.js (collection FILTER drawer package:
   Sort By open by default, Price sort labels, applied/pending filter counts, APPLY press class).

   Why it rides here: layout/theme.liquid is 42KB and re-uploading it through the API is the
   known failure mode, and the obvious hook -- snippets/mg-oos-gap-fix-include.liquid -- turns out
   not to be rendered anywhere (theme.liquid links that CSS directly). This file is small, already
   loaded site-wide, and unrelated enough that a bad edit here cannot break anything structural.
   The matching stylesheet is pulled in the same spirit, by an @import at the top of
   assets/mg-oos-gap-fix.css.

   The asset base is read off an existing /assets/ script tag rather than hardcoded, so it stays
   correct across theme IDs and CDN paths.

   >>> BUMP VERSION BELOW EVERY TIME YOU EDIT mg-filter-drawer.js. <<<
   This URL is built by hand, so it carries no Liquid asset_url fingerprint, and Shopify's CDN
   caches /assets/ hard -- without the token an edited file keeps serving the old copy (confirmed
   live on 14 Aug with the CSS twin of this problem). Same note at the top of mg-oos-gap-fix.css.
   20260814-5 = filter drawer package.
   20260816-1 = fixed-width bracket count attempt (count emitted in a nested <span class="mg-cnt">).
                ⚠️ BROKE THE FLOATING FILTER BUTTON'S LAYOUT. Do not reuse.
   20260816-2 = reverted to the flat "[2] Filter" string.
   20260816-3 = fixed-width bracket count, done properly: the count is an <i class="mg-cnt">, so
                mg-filter-chat.css's `.fillter_button span { width/height !important }` descendant
                rule cannot match it. That rule was the never-established cause of the -1 failure.
                Current. (The later 10-19 tightening was a change to THIS file only, which is
                Liquid-fingerprinted, so it needed no token bump.)
   20260822-1 = keyboard access for the drawer (item 5 in mg-filter-drawer.js): focusable trigger,
                close and accordion headers, aria state, Escape, focus trap, focus return,
                inert-when-closed and a scroll lock. Current.

   Guarded so it only loads on pages that actually have the drawer.
   ------------------------------------------------------------------------------------------ */
(function () {
  var VERSION = '20260822-1';
  if (!document.querySelector('.fillter_button, .collection__filter_inner')) return;
  var ref = document.querySelector('script[src*="/assets/"]');
  if (!ref) return;
  var s = document.createElement('script');
  s.src = ref.src.replace(/\/assets\/.*$/, '/assets/') + 'mg-filter-drawer.js?v=' + VERSION;
  s.defer = true;
  document.head.appendChild(s);
})();

/* ------------------------------------------------------------------------------------------
   MG 14-15 Aug 2026 -- loader for assets/mg-header-sticky-desktop.js
   Steps 1 and 2a of the header scroll-smoothness work. Same rationale for riding here as the
   loader above: theme.liquid is 42KB and re-uploading it is the known failure mode. Its matching
   stylesheet is pulled by an @import at the top of assets/mg-oos-gap-fix.css.

   The script self-guards (desktop only, and it bails if the CSS did not land), so this loader
   stays deliberately dumb -- keeping the scope in ONE place.

   >>> BUMP VERSION BELOW EVERY TIME YOU EDIT mg-header-sticky-desktop.js. <<<
   Hand-built URL, no Liquid fingerprint, hard CDN caching -- see the note above.
   -1 = first deploy.
   -2 = lazy wrapper resolution (the wrapper is built by jQuery .sticky() AFTER this script
        runs, so -1 bailed and the header never changed state).
   -3 = homepage guard fixed: classList.contains('index') is FALSE because Liquid bakes raw
        newlines into the body class token (it is literally `index\n\n`). Use `template_index`.
   -4 = Step 2a: homepage branch added (1px sentinel, because the homepage .header-section is
        height:0 and IntersectionObserver ratios are unreliable on a zero-area target).

   >>> ROLLBACK STEP 1: delete THIS WHOLE BLOCK, and the mg-header-sticky-desktop.css @import
       line in assets/mg-oos-gap-fix.css.
       To roll back the HOMEPAGE ONLY, leave this block alone and delete the STEP 2a blocks
       inside mg-header-sticky-desktop.css and .js instead. <<<
   ------------------------------------------------------------------------------------------ */
(function () {
  var VERSION = '20260815-4';
  var ref = document.querySelector('script[src*="/assets/"]');
  if (!ref) return;
  var s = document.createElement('script');
  s.src = ref.src.replace(/\/assets\/.*$/, '/assets/') + 'mg-header-sticky-desktop.js?v=' + VERSION;
  s.defer = true;
  document.head.appendChild(s);
})();

/* ------------------------------------------------------------------------------------------
   MG 16 Aug 2026 (Will sss) -- loader for assets/mg-card-image-preload.js.

   Collection card images were blank for ~1000ms while scrolling. That file asks lazysizes to
   unveil a card image 800px BEFORE it scrolls into view, because lazysizes CANNOT be reconfigured
   at runtime -- it captures throttleDelay and ricTimeout into a closure at init and caches expand
   in loader._defEx. Full derivation, the measured before/after on both desktop and Fast 4G, and
   the bounce-cost table behind the 800px choice, are all in the head of that file. Its CSS half
   (which halves the post-load opacity fade to 0.25s) is mg-card-image-preload.css, pulled in by
   the @import list at the top of assets/mg-oos-gap-fix.css.

   Rides here for the same reason as the two loaders above: theme.liquid is 42KB and re-uploading
   it is the known failure mode.

   >>> BUMP VERSION BELOW EVERY TIME YOU EDIT mg-card-image-preload.js. <<<
   Hand-built URL, no Liquid fingerprint, hard CDN caching -- see the note above.
   20260816-1 = first deploy (rootMargin 800px).
   20260828-1 = SECONDARY (hover-swap) images held back: primaries keep the 800px lookahead,
                secondaries get 200px and are not unveiled until after `load`, at idle. Current.
                ⚠️ VERIFIED 28 Aug that this token bump is REQUIRED, not precautionary: with the
                new file already uploaded, a no-store fetch of ...?v=20260816-1 still returned the
                OLD minified bytes while ...?v=20260828-1 returned the new ones. The CDN caches
                per URL, query string included.

   Guarded to pages that actually render a product grid.
   >>> ROLLBACK: delete THIS WHOLE BLOCK. The .js and .css assets can stay; nothing else
       references them. <<<
   ------------------------------------------------------------------------------------------ */
(function () {
  var VERSION = '20260828-1';
  if (!document.querySelector('.product__grid-item')) return;
  var ref = document.querySelector('script[src*="/assets/"]');
  if (!ref) return;
  var s = document.createElement('script');
  s.src = ref.src.replace(/\/assets\/.*$/, '/assets/') + 'mg-card-image-preload.js?v=' + VERSION;
  s.defer = true;
  document.head.appendChild(s);
})();